import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, Link, Typography } from '@mui/material';
import { mailApi } from '../../services/api';
import { InboundMessage, MailClassification } from '../../types';
import MessageList from './MessageList';

interface Props {
  applicationId: string;
  /** Called after a correction, which can move the application's status. */
  onChanged: () => void;
}

/** The employer emails JobFlo placed on this application. Renders nothing until there is one. */
const ApplicationMail: React.FC<Props> = ({ applicationId, onChanged }) => {
  const [messages, setMessages] = useState<InboundMessage[] | null>(null);

  const load = useCallback(async () => {
    try {
      setMessages((await mailApi.messages({ application: applicationId })).results);
    } catch {
      // Email tracking is an extra on this page; the page works without it.
      setMessages([]);
    }
  }, [applicationId]);

  useEffect(() => {
    load();
  }, [load]);

  const change = async (message: InboundMessage, data: { classification?: MailClassification }) => {
    await mailApi.updateMessage(message.id, data);
    await load();
    onChanged();
  };

  if (!messages || messages.length === 0) return null;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Emails
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Employer mail about this application, from{' '}
          <Link component={RouterLink} to="/email-tracking">
            email tracking
          </Link>
          . Correct what a message says if it is wrong.
        </Typography>
        <MessageList messages={messages} onChange={change} empty="" />
      </CardContent>
    </Card>
  );
};

export default ApplicationMail;
